import React, {useCallback, useMemo, useState} from 'react';

import {isDeepEqual} from 'src/utils/isDeepEqual';

import {ValidationContext} from './validation-context';

import type {ValidationError} from './validation-context';

export const ValidationContextProvider = ({children}: {children: React.ReactNode}) => {
    const [validationErrors, setValidationErrors] = useState<Array<ValidationError>>([]);

    const addError = useCallback((validationError: ValidationError) => {
        setValidationErrors(errors => [
            ...errors.filter(error => !isDeepEqual(
                {prev: error, current: validationError},
            )),
            validationError,
        ]);
    }, []);

    const removeError = useCallback((controlId: string, codes?: Array<string>) => {
        setValidationErrors(errors => errors.filter(error => {
            if (error.controlId === controlId) {
                if (codes) {
                    return !codes.includes(error.code);
                } else {
                    return false;
                }
            }

            return true;
        }));
    }, []);

    const getErrorsByControlId = useCallback((controlId: string) => (
        validationErrors
            .filter(error => controlId === error.controlId)
            .sort((a, b) => (b.priority || 0) - (a.priority || 0))
    ), [validationErrors]);

    const clearErrors = useCallback(() => {
        setValidationErrors([]);
    }, [setValidationErrors]);

    const value = useMemo(() => ({
        validationErrors,
        addError,
        removeError,
        getErrorsByControlId,
        clearErrors,
    }), [validationErrors, addError, removeError, getErrorsByControlId, clearErrors]);

    return (
        <ValidationContext.Provider value={value}>
            {children}
        </ValidationContext.Provider>
    );
};
